"use client"

import { useAtom } from "jotai"
import { HeadsetIcon, SendHorizonalIcon } from "lucide-react"
import Link from "next/link"
import type { ComponentProps, HTMLAttributes } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { config } from "@/config"
import { cn } from "@/utils/cn"
import { Alert, AlertDescription, AlertTitle } from "../ui/alert"
import { buttonVariants } from "../ui/button"
import { showSupportDialogAtom } from "./atoms"

interface SupportDialogProps extends ComponentProps<typeof Dialog> {
  dialogContentProps?: ComponentProps<typeof DialogContent>
}
export function SupportDialog({ dialogContentProps, ...props }: SupportDialogProps) {
  const [showSupportDialog, setShowSupportDialog] = useAtom(showSupportDialogAtom)

  return (
    <Dialog open={showSupportDialog} onOpenChange={setShowSupportDialog} {...props}>
      <DialogContent
        {...dialogContentProps}
        className={cn("sm:max-w-xl", dialogContentProps?.className)}
      >
        <DialogHeader>
          <DialogTitle>Support & Feedback</DialogTitle>
          <DialogDescription>
            I&apos;m here to help. Don&apos;t hesitate to reach out to us.
          </DialogDescription>
        </DialogHeader>

        <SupportDialogBody />
      </DialogContent>
    </Dialog>
  )
}

interface SupportDialogBodyProps extends HTMLAttributes<HTMLDivElement> {}
export function SupportDialogBody({ className, ...props }: SupportDialogBodyProps) {
  return (
    <div className={cn("flex @md:grid @md:grid-cols-2 flex-col gap-3", className)} {...props}>
      {/* WhatsApp */}
      <Alert variant="success" className="col-span-2">
        <HeadsetIcon />
        <AlertTitle>WhatsApp</AlertTitle>
        <AlertDescription className="mb-2.5 text-xs!">
          Chat with the founder
          <Link href={config.links.linkedin} target="_blank">
            {" "}
            Felix{" "}
          </Link>
          and get instant help.
        </AlertDescription>
        <a
          href={config.links.whatsapp}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={buttonVariants({
            variant: "default",
            size: "xs",
            className: "-mb-1 -mx-2.5 mt-auto px-0!",
          })}
        >
          Start Chat
        </a>
      </Alert>

      {/* Discord */}
      {/* <Alert variant="discord">
        <MessagesSquareIcon />
        <AlertTitle>Private Discord</AlertTitle>
        <AlertDescription className="mb-2.5 text-xs!">
          Get help, discuss features, and chat with others.
        </AlertDescription>
        <a
          href={config.links.discord}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={buttonVariants({
            variant: "default",
            size: "xs",
            className: "-mb-1 -mx-2.5 mt-auto px-0!",
          })}
        >
          Join Server
        </a>
      </Alert> */}

      {/* Email */}
      <Alert
        variant="default"
        size="sm"
        className="col-span-2 flex flex-row items-center justify-between"
      >
        <SendHorizonalIcon className="top-[15.5px]! left-[19px]!" />
        <AlertTitle>Support E-Mail</AlertTitle>
        <a
          href={config.emails.supportLink}
          target="_blank"
          className={buttonVariants({
            variant: "outline",
            size: "xs",
            className: "-my-1 -mr-2 ml-auto px-6!",
          })}
        >
          Write to us
        </a>
      </Alert>

      {/* Email */}
      <Alert
        variant="default"
        size="sm"
        className="col-span-2 flex flex-row items-center justify-between"
      >
        <div className="mr-[9px] ml-[2px] font-semibold text-xl leading-none">𝕏</div>
        <AlertTitle>Twitter</AlertTitle>
        <a
          href={config.links.twitter}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={buttonVariants({
            variant: "outline",
            size: "xs",
            className: "-my-1 -mr-2 ml-auto px-6!",
          })}
        >
          Write to us
        </a>
      </Alert>
    </div>
  )
}
